import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import toTitle from '../../utils/toTitle';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { useModalManager } from '../../hooks/ModalManager.hook';
import { setSelectedAttachmentAction } from '../../redux/Attachment/Attachment.slice';
import { AttachEditState } from '../../redux/Attachment/Attachment.type';

import UserBadgeComponent from '../UserBadge.component';
import { Dropzone } from '../Dropzone.component';
import ButtonComponent from '../Button.component';
import AttachmentEditModal from '../Modal/Attachment/AttachmentEditModal';

import { ATTACHMENTS_BIG_ICONS } from '../Icons/Attachments.icons';
import ChevronRightIcon from '../Icons/ChevronRight.icon';
import CatGlassIcon from '../Icons/Cat/CatGlass.icon';
import { StyledBaseIcon } from '../Icons/Base.icon';

import { DATE_TIME_FORMAT } from '../../constants/date.contant';
import TooltipComponent from '../Tooltip.component';
import { StyledTooltipWrapper } from '../../styles';

export interface RecordEditModalAttachProps {
  value: AttachEditState[];
  onChangeAttachments: Function;
}

const MAX_ATTACHMENTS: number = 20;

const StyledEmptyAttachTitle = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
  padding: 20px;
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledEmptyAttach = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  width: 100%;
  height: 100%;

  & > ${StyledBaseIcon} {
    opacity: 0.1;
  }
`;
const StyledAttachItemAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 5px 0 0 0;
`;
const StyledAttachItemDetailItem = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  font-size: 12px;
`;
const StyledAttachItemDetailItemWait = styled(StyledAttachItemDetailItem)`
  color: ${({ theme }) => theme.colors.ORANGE.O400};
`;
const StyledAttachItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12px;
  overflow: hidden;
`;
const StyledAttachItemType = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledAttachFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.BORDER};
  padding: 10px;
`;
const StyledAttachHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
  font-weight: 500;
  font-size: 16px;
  line-height: 32px;
`;
const StyledAttachItem = styled.div`
  display: grid;
  grid-template-columns: 52px 1fr 20px;
  align-items: center;
  padding: 5px 0;
  margin: 0 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.LABEL};
  transition: all 0.3s;
  border: 1px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  min-height: 90px;

  :hover {
    background: ${({ theme }) => theme.colors.BLUE.B50};
    color: ${({ theme }) => theme.colors.BLUE.B400};
    border-color: ${({ theme }) => theme.colors.BLUE.B400};
  }
`;
const StyledAttachContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: flex-start;
  overflow: auto;
  flex: 1 1;
  height: 100%;
`;
const StyledEditModalAttachWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  height: 100%;
`;
const StyledEditModalAttachContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
`;

const StyledTruncatedContainer = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ModalAttachBlockComponent = ({
  value: _attachments,
  onChangeAttachments,
}: RecordEditModalAttachProps) => {
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const [attachments, setAttachments] = useState(_attachments);
  const { t } = useTranslation();

  useEffect(() => {
    if (!_attachments) return;
    setAttachments(
      _attachments.map((attach: AttachEditState) => ({
        ...attach,
        tmpUUID: attach.uuid || attach.tmpUUID,
      })),
    );
  }, [_attachments]);

  const handleOpenAttachEdit = (attach?: AttachEditState) => {
    dispatch(setSelectedAttachmentAction(attach));

    openModal(AttachmentEditModal, {
      handleChange: handleAttachmentChange,
      handleClose: () => {
        dispatch(setSelectedAttachmentAction(undefined));
      },
    });
  };

  const handleAttachmentChange = (form: any) => {
    const isAlready = attachments.find((attach: AttachEditState) =>
      form.tmpUUID ? form.tmpUUID === attach.tmpUUID : false,
    );
    let newAttachments = [...attachments];
    if (isAlready) {
      newAttachments = newAttachments.filter((attachment: AttachEditState) => {
        if (attachment.tmpUUID === isAlready.tmpUUID) {
          return !(form.isDeleted && !attachment.uuid);
        }

        return true;
      });
      newAttachments = newAttachments.map((attachment: AttachEditState) => {
        if (attachment.tmpUUID === isAlready.tmpUUID) {
          return {
            ...attachment,
            type: form.type.value,
            url: form.url,
            file: form.file,
            isPrivate: form.isPrivate,
            isChanged: true,
            isDeleted: form.isDeleted,
          };
        }

        return attachment;
      });
    } else {
      newAttachments = [
        ...newAttachments,
        {
          ...form,
          type: form.type.value,
          isChanged: true,
        },
      ];
    }

    onChangeAttachments(newAttachments);
  };

  const onDrop = useCallback(
    (files: File[]) => {
      openModal(AttachmentEditModal, {
        file: files[0],
        handleChange: handleAttachmentChange,
        handleClose: () => {
          dispatch(setSelectedAttachmentAction(undefined));
        },
      });
    },
    [handleAttachmentChange],
  );

  return (
    <Dropzone onDrop={onDrop}>
      <StyledEditModalAttachContainer>
        {attachments.length > 0 ? (
          <StyledEditModalAttachWrapper>
            <StyledAttachHeader>{t('Attachments')}</StyledAttachHeader>
            <StyledAttachContent>
              {attachments.map((attachment: any) => (
                <StyledAttachItem
                  key={attachment.tmpUUID || attachment.uuid}
                  onClick={() => handleOpenAttachEdit(attachment)}
                >
                  <StyledAttachItemType>
                    {ATTACHMENTS_BIG_ICONS[attachment.type]}
                  </StyledAttachItemType>
                  <StyledAttachItemDetails>
                    <StyledAttachItemDetailItem>
                      <span>{t('Type')}:</span>
                      <strong>{t(toTitle(attachment.type))}</strong>
                    </StyledAttachItemDetailItem>
                    {attachment.file && (
                      <StyledAttachItemDetailItem>
                        <span>{t('File')}:</span>
                        <StyledTruncatedContainer>
                          {attachment.file?.name}
                        </StyledTruncatedContainer>
                      </StyledAttachItemDetailItem>
                    )}
                    {attachment.url && (
                      <StyledAttachItemDetailItem>
                        <span>{t('Url')}:</span>
                        <StyledTruncatedContainer>
                          {attachment.url}
                        </StyledTruncatedContainer>
                      </StyledAttachItemDetailItem>
                    )}
                    {(attachment.updatedAt || attachment.createdAt) && (
                      <StyledAttachItemDetailItem>
                        <span>{t('Date')}:</span>
                        <strong>
                          {dayjs(
                            attachment.updatedAt || attachment.createdAt,
                          ).format(DATE_TIME_FORMAT)}
                        </strong>
                      </StyledAttachItemDetailItem>
                    )}
                    {attachment.isPrivate && (
                      <StyledAttachItemDetailItemWait>
                        <span>{t('Available only to me')}</span>
                      </StyledAttachItemDetailItemWait>
                    )}
                    {!attachment.isChanged && (
                      <StyledAttachItemAuthor>
                        <UserBadgeComponent info={attachment.author} />
                      </StyledAttachItemAuthor>
                    )}
                    {attachment.isChanged && !attachment.isDeleted && (
                      <StyledAttachItemDetailItemWait>
                        {t('Waiting save')}
                      </StyledAttachItemDetailItemWait>
                    )}
                    {attachment.isChanged && attachment.isDeleted && (
                      <StyledAttachItemDetailItemWait>
                        {t('Waiting deletion')}
                      </StyledAttachItemDetailItemWait>
                    )}
                  </StyledAttachItemDetails>
                  <ChevronRightIcon />
                </StyledAttachItem>
              ))}
            </StyledAttachContent>
            <StyledAttachFooter>
              <StyledTooltipWrapper>
                <ButtonComponent
                  disabled={attachments?.length >= MAX_ATTACHMENTS}
                  handleClick={() => handleOpenAttachEdit()}
                >
                  {t('Add attachment')}
                </ButtonComponent>
                {attachments?.length >= MAX_ATTACHMENTS && (
                  <TooltipComponent
                    tooltip={t('Maximum number of applications:', {
                      max: MAX_ATTACHMENTS,
                    })}
                  />
                )}
              </StyledTooltipWrapper>
            </StyledAttachFooter>
          </StyledEditModalAttachWrapper>
        ) : (
          <StyledEmptyAttach>
            <StyledEmptyAttachTitle>
              {t('attach.modalTitle')}
            </StyledEmptyAttachTitle>
            <CatGlassIcon />
            <ButtonComponent handleClick={() => handleOpenAttachEdit()}>
              {t('Add attachment')}
            </ButtonComponent>
          </StyledEmptyAttach>
        )}
      </StyledEditModalAttachContainer>
    </Dropzone>
  );
};

export default ModalAttachBlockComponent;
